/**
 * Unified WebSocket Service
 * 
 * Centralized WebSocket management to prevent component remounting issues.
 * Replaces multiple WebSocket implementations with a single, persistent service.
 * 
 * Features:
 * - Singleton pattern for app-wide WebSocket management
 * - Automatic reconnection with exponential backoff
 * - Connection pooling and state management
 * - Redux-first state updates (no direct cache invalidation)
 * - Subscription management for different data types
 */

import { io } from 'socket.io-client';
import axios from '@configs/axios';
import { store } from '../redux/store';
import {
  setConnectionState,
  setMarketData,
  setWalletData,
  setOrderData,
  setNotificationData
} from '../redux/websocket';
import { handleOrderUpdater, handleWalletUpdater } from '../redux/notification';
import { logWebSocket, logInfo, logError } from '../utils/logger';
import cacheUpdateService from './CacheUpdateService';
import { PERFORMANCE_TEST_CONFIG, logPerformanceTest } from '../config/performanceTest';
import { normalizeWebSocketNotification } from '../utils/notificationNormalizer';
import {
  CircuitBreaker,
  ConnectionQualityMonitor,
  calculateBackoffWithJitter,
  getAdaptiveReconnectDelay
} from './ConnectionReliability';
// 🔥 PHASE 2: Message Processing
import {
  validateMessage,
  validateAndParseJSON,
  sanitizeMessage,
  MessageValidationStats
} from './MessageValidator';
import { MessageAcknowledgmentManager } from './MessageAcknowledgment';
import { MessageProcessor } from './MessageProcessor';

// 🔥 PHASE 3: State Management
import { StateManager } from './StateManager';
import { StateReconciliationManager, MergeStrategy } from './StateReconciliation';

// 🔥 PHASE 4: Error Handling
import { HeartbeatMetrics } from './HeartbeatMetrics';
import { ErrorHandler, ErrorType, ErrorSeverity, RetryStrategy } from './ErrorHandler';
import { GracefulDegradationManager, HealthStatus, FeatureFlag } from './GracefulDegradation';

class WebSocketService {
  constructor() {
    if (WebSocketService.instance) {
      return WebSocketService.instance;
    }

    this.connections = new Map(); // Multiple connections for different servers
    this.subscriptions = new Map(); // Active subscriptions
    this.reconnectAttempts = new Map(); // Track reconnection attempts per server
    this.isInitialized = false;
    this.connectionStates = new Map();

    // Track current subscriptions for context-aware caching
    this.currentDealsSubscription = null;
    this.currentOrderbookSubscription = null;

    // Track last used token to prevent reconnection with same invalid token
    this.lastUsedTokens = new Map(); // serverKey -> token
    this.authErrorCounts = new Map(); // serverKey -> count

    // Throttle state for WebSocket message handlers
    this.throttleState = new Map(); // event -> { lastUpdate, pending }

    // 🔥 PHASE 1: Connection Reliability
    // Circuit breakers for each server to prevent excessive reconnection attempts
    this.circuitBreakers = new Map(); // serverKey -> CircuitBreaker
    // Connection quality monitors for each server
    this.qualityMonitors = new Map(); // serverKey -> ConnectionQualityMonitor
    // Heartbeat timers for quality monitoring
    this.heartbeatTimers = new Map(); // serverKey -> intervalId
    // 🔥 PHASE 4: Heartbeat metrics tracking
    this.heartbeatMetrics = new Map(); // serverKey -> HeartbeatMetrics

    // 🔥 PHASE 2: Message Processing
    // Message validation statistics
    this.validationStats = new MessageValidationStats();
    // Message acknowledgment manager
    this.ackManager = new MessageAcknowledgmentManager({
      maxRetries: 3,
      retryDelay: 1000,
      ackTimeout: 5000,
      enabled: true // Enable acknowledgments
    });
    // Message processor for deduplication
    this.messageProcessor = new MessageProcessor({
      maxCacheSize: 1000,
      cacheExpiry: 3600000, // 1 hour
      enableDeduplication: true
    });

    // 🔥 PHASE 3: State Management
    // State manager for localStorage persistence
    this.stateManager = new StateManager({
      storagePrefix: 'bitpanel_',
      maxNotifications: 100,
      syncInterval: 60000, // 1 minute
      enablePersistence: true
    });
    // State reconciliation manager
    this.reconciliationManager = new StateReconciliationManager({
      defaultStrategy: MergeStrategy.MERGE_UNION,
      enableOptimisticUpdates: true,
      conflictResolutionTimeout: 5000 // 5 seconds
    });

    // 🔥 PHASE 4: Error Handling
    // Error handler for comprehensive error handling
    this.errorHandler = new ErrorHandler({
      maxRetries: 3,
      baseDelay: 1000, // 1 second
      maxDelay: 30000, // 30 seconds
      retryStrategy: RetryStrategy.EXPONENTIAL_BACKOFF,
      enableUserNotifications: true,
      enableOfflineQueue: true,
      maxQueueSize: 100
    });
    // Graceful degradation manager
    this.degradationManager = new GracefulDegradationManager({
      degradationThreshold: 3, // Degrade after 3 consecutive failures
      recoveryThreshold: 2, // Recover after 2 consecutive successes
      healthCheckInterval: 30000, // 30 seconds
      enableAutoRecovery: true
    });

    // Configuration
    this.config = {
      maxReconnectAttempts: 5,
      maxAuthErrorRetries: 3, // Max retries for auth errors before giving up
      reconnectDelay: 2000,
      maxReconnectDelay: 30000,
      authErrorRetryDelay: 5000, // Longer delay for auth errors
      heartbeatInterval: 30000,
      subscriptionTimeout: 10000,
      // Performance optimization: throttle WebSocket updates
      throttleMarketData: 100, // 100ms = max 10 updates/second
      throttleOrderbook: 200, // 200ms = max 5 updates/second
      throttleDeals: 500, // 500ms = max 2 updates/second
      throttleTicker: 1000 // 1s = max 1 update/second
    };

    // Sleep/wake detection for UI refresh
    this.lastActivityTime = Date.now();
    this.sleepThreshold = 60000; // 1 minute
    this.lastRecoveryTime = 0; // Track last recovery to prevent rapid repeated calls
    this.recoveryThrottle = 5000; // Minimum 5 seconds between recovery attempts
    this.forceRefreshCallbacks = new Set();

    // Sleep/wake detection for UI refresh
    this.lastActivityTime = Date.now();
    this.sleepThreshold = 60000; // 1 minute
    this.forceRefreshCallbacks = new Set();

    // Server configurations with failover support
    // Using standard Socket.IO transport order: polling first, then upgrade to websocket
    this.servers = {
      notifications: {
        url: process.env.REACT_APP_NOTIFICATIONS_URL || 'https://notif2.bitazar.com',
        transports: ['polling', 'websocket'], // Standard: polling → websocket upgrade
        auth: true // Requires authentication token
      },
      trading: {
        // Primary and fallback servers for auto-switching
        primary: {
          url: process.env.REACT_APP_SOCKET_TYPE === 'germany'
            ? (process.env.REACT_APP_GERMANY_WEBSOCKET_URL || 'https://websv2.bitazar.com')
            : (process.env.REACT_APP_IRAN_WEBSOCKET_URL || 'https://devengine.bitazar.com'),
          name: process.env.REACT_APP_SOCKET_TYPE === 'germany' ? 'Germany' : 'Iran',
          transports: ['polling', 'websocket'], // Standard: polling → websocket upgrade
          auth: false
        },
        fallback: {
          url: process.env.REACT_APP_SOCKET_TYPE === 'germany'
            ? (process.env.REACT_APP_IRAN_WEBSOCKET_URL || 'https://devengine.bitazar.com')
            : (process.env.REACT_APP_GERMANY_WEBSOCKET_URL || 'https://websv2.bitazar.com'),
          name: process.env.REACT_APP_SOCKET_TYPE === 'germany' ? 'Iran' : 'Germany',
          transports: ['polling', 'websocket'], // Standard: polling → websocket upgrade
          auth: false
        }
      },
      // Market data namespace for Kafka-based real-time data
      market: {
        url: process.env.REACT_APP_NOTIFICATIONS_URL || 'https://notif2.bitazar.com',
        namespace: '/market', // Separate namespace on NotifEngine
        transports: ['polling', 'websocket'], // Standard: polling → websocket upgrade
        auth: false // No authentication required for public market data
      }
    };

    // Market data subscriptions and callbacks
    this.marketSubscriptions = new Map(); // channel -> Set of callbacks
    this.marketCallbacks = {
      orderbook: new Map(), // marketName -> callback
      trades: new Map(), // marketName -> callback
      marketData: new Map(), // marketName -> callback
      kline: new Map(), // marketName@interval -> callback
      orderEvents: new Map() // marketName -> callback
    };

    WebSocketService.instance = this;

    // Add global debugging functions in development
    if (process.env.NODE_ENV === 'development') {
      window.webSocketService = this;
      window.testIranSubscriptions = () => this.testIranSubscriptions();
    }

    // Setup sleep/wake detection
    this.setupSleepWakeDetection();

    // Setup token change detection
    this.setupTokenChangeDetection();
  }

  /**
   * Throttle WebSocket message handler to prevent excessive Redux updates
   * @param {string} eventKey - Unique key for this event type
   * @param {number} throttleMs - Throttle duration in milliseconds
   * @param {Function} callback - Function to call with the data
   * @param {*} data - Data to pass to callback
   */
  throttleUpdate(eventKey, throttleMs, callback, data) {
    const now = Date.now();
    const state = this.throttleState.get(eventKey) || { lastUpdate: 0, pending: null };

    // If enough time has passed, update immediately
    if (now - state.lastUpdate >= throttleMs) {
      state.lastUpdate = now;
      state.pending = null;
      this.throttleState.set(eventKey, state);
      callback(data);
      return;
    }

    // Otherwise, schedule an update for later (debounce)
    if (state.pending) {
      clearTimeout(state.pending);
    }

    state.pending = setTimeout(() => {
      state.lastUpdate = Date.now();
      state.pending = null;
      this.throttleState.set(eventKey, state);
      callback(data);
    }, throttleMs - (now - state.lastUpdate));

    this.throttleState.set(eventKey, state);
  }

  /**
   * Initialize the WebSocket service
   */
  initialize() {
    if (this.isInitialized) {
      logWebSocket('WebSocketService already initialized');
      return;
    }

    logInfo('Initializing unified WebSocket service');

    // Initialize connection states
    Object.keys(this.servers).forEach(serverKey => {
      this.connectionStates.set(serverKey, {
        connected: false,
        connecting: false,
        error: null,
        lastConnected: null,
        reconnectAttempts: 0,
        usingFallback: false,
        currentServer: null
      });
    });

    this.isInitialized = true;

    // Auto-connect to servers based on user authentication
    this.autoConnect();
  }

  /**
   * Auto-connect to appropriate servers
   */
  async autoConnect() {
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    const isAuthenticated = !!userData.accessToken;

    if (isAuthenticated) {
      logInfo('User authenticated, connecting to all servers');
      await this.connect('notifications');
      await this.connect('trading');
    } else {
      logInfo('User not authenticated, connecting to trading server only');
      await this.connect('trading');
    }
  }

  /**
   * Check if JWT token is expired or too old for WebSocket connection
   *
   * IMPORTANT: The notifengine server has maxAge: '24h' which rejects tokens
   * issued more than 24 hours ago, even if they haven't expired yet.
   *
   * This can happen if we accidentally store a refresh_token (1-year expiration)
   * as the access_token.
   */
  isTokenExpired(token) {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const now = Date.now() / 1000;

      // Check if token is expired (with 30 second buffer)
      const isExpired = now >= (payload.exp - 30);

      // Check if token is too old (issued more than 24 hours ago)
      // The notifengine server rejects tokens older than 24h even if not expired
      const tokenAgeHours = ((now - payload.iat) / 3600).toFixed(1);
      const isTooOld = (now - payload.iat) > (24 * 3600); // Older than 24 hours

      // Detailed logging
      const issuedAt = new Date(payload.iat * 1000).toISOString();
      const expiresAt = new Date(payload.exp * 1000).toISOString();
      const currentTime = new Date(now * 1000).toISOString();
      const minutesSinceExpiry = ((now - payload.exp) / 60).toFixed(1);

      console.log('🔐 Token Expiration Check:', {
        issuedAt,
        expiresAt,
        currentTime,
        tokenAgeHours,
        isExpired,
        isTooOld,
        minutesSinceExpiry: isExpired ? minutesSinceExpiry : 'N/A (not expired yet)',
        tokenPreview: token.substring(0, 30) + '...'
      });

      if (isExpired) {
        console.log(`🔐 ⚠️ Token EXPIRED ${minutesSinceExpiry} minutes ago!`);
      } else if (isTooOld) {
        console.log(`🔐 ⚠️ Token is ${tokenAgeHours} hours old - TOO OLD for WebSocket (max 24h)!`);
        console.log(`🔐 ⚠️ This might be a refresh_token (1-year expiration) instead of access_token (24h expiration)`);
      } else {
        const minutesUntilExpiry = ((payload.exp - now) / 60).toFixed(1);
        console.log(`🔐 ✅ Token still valid for ${minutesUntilExpiry} minutes (age: ${tokenAgeHours}h)`);
      }

      return isExpired || isTooOld;
    } catch (error) {
      console.error('❌ Error checking token expiration:', error);
      return true; // Assume expired if we can't parse it
    }
  }

  /**
   * Refresh token using jwtService
   *
   * IMPORTANT: We can't rely on /user/access_status returning 401 for expired tokens
   * because the server has a bug where it accepts expired tokens.
   * Instead, we manually call the refresh token endpoint when we detect expiration.
   */
  async refreshTokenForWebSocket() {
    try {
      console.log('🔐 ========================================');
      console.log('🔐 Starting token refresh for WebSocket...');

      // Store the old token to compare later
      const oldUserData = JSON.parse(localStorage.getItem('userData') || '{}');
      const oldToken = oldUserData.accessToken;

      // Refresh token is stored separately in localStorage (not in userData object)
      const oldRefreshToken = localStorage.getItem('refreshToken') || oldUserData.refreshToken;

      console.log('🔐 Old token preview:', oldToken ? oldToken.substring(0, 30) + '...' : 'NONE');
      console.log('🔐 Refresh token available?', oldRefreshToken ? 'YES ✅' : 'NO ❌');

      // Check if old token is expired
      let isOldTokenExpired = false;
      if (oldToken) {
        isOldTokenExpired = this.isTokenExpired(oldToken);
        console.log('🔐 Old token expired?', isOldTokenExpired ? 'YES ❌' : 'NO ✅');
      }

      // If token is expired, force a refresh by calling the refresh endpoint directly
      if (isOldTokenExpired) {
        console.log('🔐 ⚠️ Token is expired - forcing refresh via /refresh-token endpoint');
        console.log('🔐 (Cannot rely on /user/access_status returning 401 due to server bug)');

        if (!oldRefreshToken) {
          console.error('❌ No refresh token available - cannot refresh');
          console.error('❌ Please login again to get a new token');
          console.log('🔐 ========================================');
          return false;
        }

        try {
          // Call the refresh token endpoint directly
          console.log('🔐 Calling POST /refresh-token...');
          const response = await axios.post('/refresh-token', {
            refresh_token: oldRefreshToken
          });

          console.log('🔐 Refresh token API response:', {
            status: response.status,
            code: response.data?.code,
            message: response.data?.message,
            hasData: !!response.data?.data,
            hasAccessToken: !!response.data?.data?.access_token || !!response.data?.access_token,
            dataKeys: response.data?.data ? Object.keys(response.data.data) : [],
            fullResponse: response.data
          });

          // Laravel Passport returns: { code: 200, message: '...', data: { token, refresh_token } }
          // Note: The field might be 'token' instead of 'access_token'
          const responseData = response.data?.data || response.data;
          const newAccessToken = responseData.access_token || responseData.token;
          const newRefreshToken = responseData.refresh_token || responseData.refreshToken;

          console.log('🔐 Response data keys:', Object.keys(responseData));
          console.log('🔐 access_token field:', responseData.access_token ? 'EXISTS' : 'MISSING');
          console.log('🔐 token field:', responseData.token ? 'EXISTS' : 'MISSING');
          console.log('🔐 refresh_token field:', responseData.refresh_token ? 'EXISTS' : 'MISSING');
          console.log('🔐 refreshToken field:', responseData.refreshToken ? 'EXISTS' : 'MISSING');

          if (newAccessToken) {
            console.log('🔐 ✅ Token refresh successful!');
            console.log('🔐 Using field:', responseData.access_token ? 'access_token' : 'token');

            // Decode and verify the token we're about to save
            try {
              const payload = JSON.parse(atob(newAccessToken.split('.')[1]));
              const expiresAt = new Date(payload.exp * 1000);
              const hoursUntilExpiry = (payload.exp * 1000 - Date.now()) / (1000 * 60 * 60);
              console.log('🔐 New token expiration:', expiresAt.toISOString());
              console.log('🔐 Hours until expiry:', hoursUntilExpiry.toFixed(2));
              console.log('🔐 Token audience (aud):', payload.aud);
              console.log('🔐 Token subject (sub):', payload.sub);

              if (hoursUntilExpiry > 48) {
                console.warn('⚠️ WARNING: Token expires in more than 48 hours - this might be a refresh token, not an access token!');
              }
            } catch (e) {
              console.error('❌ Failed to decode new token:', e);
            }

            // Update userData object in localStorage
            const newUserData = {
              ...oldUserData,
              accessToken: newAccessToken,
              refreshToken: newRefreshToken
            };
            localStorage.setItem('userData', JSON.stringify(newUserData));

            // Also update separate token storage locations (for compatibility)
            localStorage.setItem('accessToken', newAccessToken);
            if (newRefreshToken) {
              localStorage.setItem('refreshToken', newRefreshToken);
            }

            console.log('🔐 ✅ Updated localStorage with new tokens');
            console.log('🔐   - userData object updated');
            console.log('🔐   - accessToken updated');
            console.log('🔐   - refreshToken updated');
            console.log('🔐 New token preview:', newAccessToken.substring(0, 30) + '...');

            // Dispatch event so other parts of the app know token was refreshed
            window.dispatchEvent(new CustomEvent('tokenRefreshed', {
              detail: { accessToken: newAccessToken }
            }));

            console.log('🔐 ========================================');
            return true;
          } else {
            console.error('❌ Refresh token response missing access_token');
            console.error('❌ Response data:', response.data);
            console.log('🔐 ========================================');
            return false;
          }
        } catch (error) {
          console.error('❌ Token refresh failed:', {
            status: error.response?.status,
            statusText: error.response?.statusText,
            message: error.message,
            data: error.response?.data
          });
          console.log('🔐 ========================================');
          return false;
        }
      }

      // Token is not expired - verify with API call
      console.log('🔐 Token not expired yet - verifying with API call...');
      console.log('🔐 Making API call to /user/access_status...');

      try {
        const response = await axios.get('/user/access_status');
        console.log('🔐 ✅ API call succeeded with status:', response.status);
        console.log('🔐 Token is valid - no refresh needed');
        console.log('🔐 ========================================');
        return true;
      } catch (error) {
        console.log('🔐 ❌ API call failed with error:', {
          status: error.response?.status,
          statusText: error.response?.statusText,
          message: error.message
        });

        // If it's a 401, the interceptor will handle token refresh
        if (error.response && error.response.status === 401) {
          console.log('🔐 401 Unauthorized - Token refresh should be triggered by axios interceptor');

          // Wait for the token to actually change in localStorage
          // Poll localStorage every 100ms for up to 5 seconds
          const maxWaitTime = 5000;
          const pollInterval = 100;
          const startTime = Date.now();
          let pollCount = 0;

          console.log('🔐 Polling localStorage for token change (max 5 seconds)...');

          while (Date.now() - startTime < maxWaitTime) {
            await new Promise(resolve => setTimeout(resolve, pollInterval));
            pollCount++;

            const newUserData = JSON.parse(localStorage.getItem('userData') || '{}');
            const newToken = newUserData.accessToken;

            if (pollCount % 10 === 0) {
              console.log(`🔐 Poll #${pollCount}: Token changed?`, newToken !== oldToken ? 'YES ✅' : 'NO ❌');
            }

            if (newToken && newToken !== oldToken) {
              console.log('🔐 ✅ Token successfully refreshed and updated in localStorage!');
              console.log('🔐 New token preview:', newToken.substring(0, 30) + '...');
              console.log('🔐 Polling took:', Date.now() - startTime, 'ms');
              console.log('🔐 ========================================');
              return true;
            }
          }

          console.error('❌ Token refresh TIMEOUT - token did not update in localStorage after 5 seconds');
          console.error('❌ This means the axios interceptor did not refresh the token');
          console.log('🔐 ========================================');
          return false;
        }

        // For other errors, log and fail
        console.error('❌ API call failed with non-401 error:', error.response?.status, error.message);
        console.log('🔐 ========================================');
        return false;
      }
    } catch (error) {
      console.error('❌ Unexpected error in refreshTokenForWebSocket:', error);
      console.log('🔐 ========================================');
      return false;
    }
  }

  /**
   * Connect to a specific server with failover support
   */
  async connect(serverKey, useFallback = false) {
    if (!this.servers[serverKey]) {
      console.error(`❌ Unknown server: ${serverKey}`);
      return;
    }

    // 🔥 PHASE 1: Initialize circuit breaker if not exists
    if (!this.circuitBreakers.has(serverKey)) {
      this.circuitBreakers.set(serverKey, new CircuitBreaker({
        failureThreshold: 5,
        successThreshold: 2,
        timeout: 60000 // 1 minute
      }));
      console.log(`🔄 Circuit breaker initialized for ${serverKey}`);
    }

    // 🔥 PHASE 1: Initialize quality monitor if not exists
    if (!this.qualityMonitors.has(serverKey)) {
      this.qualityMonitors.set(serverKey, new ConnectionQualityMonitor({
        windowSize: 10
      }));
      console.log(`📊 Quality monitor initialized for ${serverKey}`);
    }

    // 🔥 PHASE 1: Check circuit breaker before attempting connection
    const circuitBreaker = this.circuitBreakers.get(serverKey);
    if (!circuitBreaker.canAttempt()) {
      const state = circuitBreaker.getState();
      console.error(`⛔ Circuit breaker is OPEN for ${serverKey} - blocking connection attempt`);
      console.log(`⏰ Next attempt allowed in ${Math.round(state.timeUntilNextAttempt / 1000)}s`);
      return;
    }

    if (this.connections.has(serverKey)) {
      const socket = this.connections.get(serverKey);
      if (socket.connected) {
        logWebSocket(`Already connected to ${serverKey}`);
        return socket;
      }
    }

    // Determine which server config to use
    let serverConfig;
    let serverName;

    if (serverKey === 'trading') {
      // For trading server, use primary/fallback logic
      if (useFallback && this.servers[serverKey].fallback) {
        serverConfig = this.servers[serverKey].fallback;
        serverName = `${serverKey} (${serverConfig.name} fallback)`;
      } else {
        serverConfig = this.servers[serverKey].primary;
        serverName = `${serverKey} (${serverConfig.name} primary)`;
      }
    } else {
      // For other servers (notifications), use direct config
      serverConfig = this.servers[serverKey];
      serverName = serverKey;
    }

    logInfo(`Connecting to ${serverName} server at ${serverConfig.url}...`);

    const socketOptions = {
      transports: serverConfig.transports,
      reconnection: false, // We handle reconnection manually
      timeout: 20000,
      forceNew: true
    };

    // Add authentication if required
    if (serverConfig.auth) {
      console.log('🔐 ========================================');
      console.log('🔐 Preparing authenticated connection for:', serverKey);

      // Always read fresh token from localStorage (not cached)
      let userData = JSON.parse(localStorage.getItem('userData') || '{}');

      if (!userData.accessToken) {
        console.warn('⚠️ No access token found in localStorage for authenticated connection');
        console.log('🔐 ========================================');
        return null; // Can't connect without token
      }

      console.log('🔐 Token found in localStorage:', userData.accessToken.substring(0, 30) + '...');

      // Check if token is expired or expiring soon
      const tokenExpired = this.isTokenExpired(userData.accessToken);

      if (tokenExpired) {
        console.log('🔐 ⚠️ Token is expired or expiring soon - refreshing before WebSocket connection...');

        // Try to refresh the token
        const refreshSuccess = await this.refreshTokenForWebSocket();

        if (!refreshSuccess) {
          console.error('❌ Failed to refresh token before WebSocket connection');
          console.log('🔐 ========================================');
          return null;
        }

        // Re-read userData after refresh
        userData = JSON.parse(localStorage.getItem('userData') || '{}');

        if (!userData.accessToken) {
          console.error('❌ Token refresh succeeded but userData not updated');
          console.log('🔐 ========================================');
          return null;
        }

        console.log('🔐 ✅ Token refreshed successfully - proceeding with WebSocket connection');
        console.log('🔐 New token preview:', userData.accessToken.substring(0, 30) + '...');
      } else {
        console.log('🔐 ✅ Token is still valid - no refresh needed');
      }

      // Check if this is the same token that failed before
      const lastToken = this.lastUsedTokens.get(serverKey);
      if (lastToken === userData.accessToken) {
        const authErrorCount = this.authErrorCounts.get(serverKey) || 0;
        console.log(`🔐 ⚠️ Using same token that failed ${authErrorCount} times before`);

        if (authErrorCount >= this.config.maxAuthErrorRetries) {
          console.error(`❌ Max auth error retries (${this.config.maxAuthErrorRetries}) reached for ${serverKey}. Token appears to be invalid. Stopping reconnection attempts.`);
          console.error('💡 Please refresh the page or login again to get a new token.');
          console.log('🔐 ========================================');
          return null; // Stop trying to reconnect
        }
      } else {
        console.log('🔐 ✅ Using new/different token for connection');
      }

      socketOptions.query = { token: userData.accessToken };
      this.lastUsedTokens.set(serverKey, userData.accessToken);

      console.log('🔐 Final token being used for WebSocket connection:');
      console.log('🔐   Preview:', userData.accessToken.substring(0, 30) + '...');
      console.log('🔐   Length:', userData.accessToken.length);
      console.log('🔐   Full token (first 100 chars):', userData.accessToken.substring(0, 100));
      console.log('🔐   Socket query will be:', { token: userData.accessToken.substring(0, 50) + '...' });

      // Verify the token we're about to use is not expired
      const finalTokenExpired = this.isTokenExpired(userData.accessToken);
      if (finalTokenExpired) {
        console.error('❌ CRITICAL: About to connect with EXPIRED token!');
        console.error('❌ This should never happen - token refresh failed silently');
        console.log('🔐 ========================================');
        return null;
      }

      console.log('🔐 ✅ Final token verification passed - connecting...');
      console.log('🔐 ========================================');
    }

    // Create socket connection with namespace if specified
    const connectionUrl = serverConfig.namespace
      ? `${serverConfig.url}${serverConfig.namespace}`
      : serverConfig.url;

    const socket = io(connectionUrl, socketOptions);
    this.connections.set(serverKey, socket);

    // Store which server we're using for this connection
    const state = this.connectionStates.get(serverKey);
    state.usingFallback = useFallback;
    state.currentServer = serverConfig.name || serverKey;
    this.connectionStates.set(serverKey, state);

    // Set up event handlers
    if (serverKey === 'market') {
      this.setupMarketHandlers(socket, serverKey);
    } else {
      this.setupSocketHandlers(socket, serverKey);
    }

    return socket;
  }

  /**
   * Set up socket event handlers
   */
  setupSocketHandlers(socket, serverKey) {
    const state = this.connectionStates.get(serverKey);

    socket.on('connect', () => {
      logInfo(`Connected to ${serverKey} server`);

      // Update connection state
      state.connected = true;
      state.connecting = false;
      state.error = null;
      state.lastConnected = Date.now();
      state.reconnectAttempts = 0;

      // Reset auth error count on successful connection
      this.authErrorCounts.set(serverKey, 0);

      // 🔥 PHASE 1: Record successful connection in circuit breaker
      const circuitBreaker = this.circuitBreakers.get(serverKey);
      if (circuitBreaker) {
        circuitBreaker.recordSuccess();
        console.log(`✅ Circuit breaker: Connection successful for ${serverKey}`);
      }

      this.connectionStates.set(serverKey, state);
      this.updateReduxConnectionState();

      // Restore subscriptions for this server
      this.restoreSubscriptions(serverKey);

      // 🔥 PHASE 1: Start enhanced heartbeat with quality monitoring
      this.startEnhancedHeartbeat(socket, serverKey);

      // 🔥 PHASE 3: Load persisted state and reconcile with server
      if (serverKey === 'notification') {
        this.loadAndReconcileState();
      }
    });

    socket.on('disconnect', (reason) => {
      logWebSocket(`Disconnected from ${serverKey} (${state.currentServer}): ${reason}`);

      state.connected = false;
      state.connecting = false;
      state.error = reason;

      // 🔥 PHASE 1: Record disconnection in quality monitor
      const qualityMonitor = this.qualityMonitors.get(serverKey);
      if (qualityMonitor) {
        qualityMonitor.recordDisconnection();
        const metrics = qualityMonitor.getMetrics();
        console.log(`📊 Connection quality after disconnect:`, metrics);
      }

      // 🔥 PHASE 1: Stop heartbeat timer
      const heartbeatTimer = this.heartbeatTimers.get(serverKey);
      if (heartbeatTimer) {
        clearInterval(heartbeatTimer);
        this.heartbeatTimers.delete(serverKey);
      }

      this.connectionStates.set(serverKey, state);
      this.updateReduxConnectionState();

      // 🔥 PHASE 3: Save current state before disconnect
      if (serverKey === 'notification') {
        this.saveCurrentState();
      }

      // Attempt reconnection if not manually disconnected
      if (reason !== 'io client disconnect') {
        // For trading server, try failover to alternative server
        if (serverKey === 'trading' && !state.usingFallback) {
          logInfo(`Primary ${state.currentServer} server disconnected, trying fallback server...`);
          this.connect(serverKey, true); // Connect to fallback server
        } else if (serverKey === 'trading' && state.usingFallback) {
          logInfo(`Fallback ${state.currentServer} server disconnected, trying primary server...`);
          this.connect(serverKey, false); // Try primary server again
        } else {
          this.scheduleReconnect(serverKey);
        }
      }
    });

    socket.on('connect_error', (error) => {
      console.error(`❌ Connection error for ${serverKey}:`, error);

      // 🔥 PHASE 1: Record failure in circuit breaker
      const circuitBreaker = this.circuitBreakers.get(serverKey);
      if (circuitBreaker) {
        circuitBreaker.recordFailure();
        const cbState = circuitBreaker.getState();
        console.log(`📊 Circuit breaker state:`, cbState);
      }

      // Check if it's a session ID error (server doesn't recognize our session)
      const errorMessage = error.message || '';
      const isSessionError = errorMessage.includes('Session ID unknown') ||
                            errorMessage.includes('session') ||
                            error.data?.message?.includes('Session ID unknown');

      if (isSessionError) {
        console.log('🔄 Session ID error detected - forcing clean reconnection with new session');

        // Disconnect completely to force new session
        socket.disconnect();
        this.connections.delete(serverKey);

        // Reconnect with fresh session after short delay
        setTimeout(() => {
          console.log('🔄 Creating new session...');
          this.connect(serverKey);
        }, 1000);

        return; // Don't continue with normal error handling
      }

      state.connected = false;
      state.connecting = false;
      state.error = error.message;

      this.connectionStates.set(serverKey, state);
      this.updateReduxConnectionState();

      // For trading server, try failover to alternative server
      if (serverKey === 'trading' && !state.usingFallback) {
        console.log(`🔄 Primary ${state.currentServer} server failed, trying fallback server...`);
        this.connect(serverKey, true); // Connect to fallback server
      } else if (serverKey === 'trading' && state.usingFallback) {
        console.log(`🔄 Fallback ${state.currentServer} server also failed, trying primary server...`);
        this.connect(serverKey, false); // Try primary server again
      } else {
        this.scheduleReconnect(serverKey);
      }
    });

    // Handle authentication and other socket errors
    socket.on('error', (error) => {
      console.error(`❌ Socket error for ${serverKey}:`, error);

      // Check if it's a session ID error
      const errorString = typeof error === 'string' ? error : JSON.stringify(error);
      const isSessionError = errorString.includes('Session ID unknown') ||
                            errorString.includes('session');

      if (isSessionError) {
        console.log('🔄 Session ID error detected - forcing clean reconnection with new session');

        // Disconnect completely to force new session
        socket.disconnect();
        this.connections.delete(serverKey);

        // Reconnect with fresh session after short delay
        setTimeout(() => {
          console.log('🔄 Creating new session...');
          this.connect(serverKey);
        }, 1000);

        return; // Don't continue with other error handling
      }

      // Check if it's an authentication error
      const isAuthError = errorString.includes('Authentication failed') ||
                         errorString.includes('Invalid or expired token') ||
                         errorString.includes('Unauthorized') ||
                         errorString.includes('Token expired');

      if (isAuthError && serverKey === 'notifications') {
        // Increment auth error count
        const currentCount = this.authErrorCounts.get(serverKey) || 0;
        this.authErrorCounts.set(serverKey, currentCount + 1);

        console.log(`🔐 Authentication error detected (attempt ${currentCount + 1}/${this.config.maxAuthErrorRetries})`);
        console.log(`🔐 Error message:`, error);

        // Update error state
        state.error = 'Authentication failed - token expired';
        this.connectionStates.set(serverKey, state);
        this.updateReduxConnectionState();

        // Check if we've exceeded max retries
        if (currentCount >= this.config.maxAuthErrorRetries) {
          console.error(`❌ Max authentication retries reached for ${serverKey}. Stopping reconnection attempts.`);
          console.error('💡 The token in localStorage appears to be permanently invalid.');
          console.error('💡 Please refresh the page or login again to get a new token.');
          return; // Stop trying
        }

        // Disconnect current socket
        socket.disconnect();
        this.connections.delete(serverKey);

        // Force token refresh before reconnecting
        console.log('🔐 Forcing token refresh before reconnection...');

        // Use exponential backoff for auth errors
        const delay = this.config.authErrorRetryDelay * Math.pow(2, currentCount);
        console.log(`🔐 Will attempt to refresh token and reconnect in ${delay}ms...`);

        setTimeout(async () => {
          console.log('🔐 Attempting to refresh token before reconnection...');

          // Force token refresh
          const refreshSuccess = await this.refreshTokenForWebSocket();

          if (refreshSuccess) {
            console.log('🔐 Token refreshed successfully - reconnecting...');
            this.connect(serverKey);
          } else {
            console.error('❌ Token refresh failed - cannot reconnect');
          }
        }, delay);
      } else {
        // Update error state for non-auth errors
        state.error = errorString;
        this.connectionStates.set(serverKey, state);
        this.updateReduxConnectionState();
      }
    });

    // Set up message handlers based on server type
    if (serverKey === 'notifications') {
      this.setupNotificationHandlers(socket);
    } else if (serverKey === 'trading') {
      this.setupTradingHandlers(socket);
    }
  }

  /**
   * Set up notification message handlers
   */
  setupNotificationHandlers(socket) {
    socket.on('message', (message) => {
      try {
        // 🔥 PHASE 2: Validate and parse JSON
        const validationResult = validateAndParseJSON(message);

        if (!validationResult.valid) {
          console.error('❌ Invalid message received:', validationResult.errors);
          this.validationStats.recordInvalid(validationResult.errors);
          return; // Skip invalid messages
        }

        const data = validationResult.data;
        this.validationStats.recordValid();

        console.log('📨 Notification received:', data);

        // 🔥 PHASE 2: Sanitize message data
        const sanitizedData = sanitizeMessage(data);

        // 🔥 PHASE 0: Normalize WebSocket notification to unified structure
        const normalizedNotification = normalizeWebSocketNotification(sanitizedData);
        console.log('✅ Normalized notification:', normalizedNotification);

        // 🔥 PHASE 2: Check for duplicate messages
        if (this.messageProcessor.isProcessed(normalizedNotification.hash)) {
          console.log(`⏭️ Skipping duplicate message: ${normalizedNotification.hash}`);
          this.messageProcessor.markDuplicate(normalizedNotification.hash);
          return; // Skip duplicate processing
        }

        // 🔥 PHASE 2: Mark message as being processed
        this.messageProcessor.markProcessing(normalizedNotification.hash, normalizedNotification.id);
        const processingStartTime = Date.now();

        // Normalize type to handle both string and numeric types
        const messageType = typeof data.type === 'string'
          ? data.type.toUpperCase()
          : data.type;

        console.log('🔍 Message type:', messageType, '(original:', data.type, ')');

        // Handle specific notification types (support both string and numeric)
        if (messageType === 'WALLET' || data.type === 1) {
          // Wallet update
          // Message format: {"type":1,"id":54003,"amount":5.464996353683,"freeze":69,"value":1.8529944536578802,"toman_amount":212816.41300260753}
          console.log('💰 Wallet update detected:', data);
          store.dispatch(setWalletData({
            type: 'websocket_update',
            data: data,
            timestamp: Date.now()
          }));

          // 🔥 PHASE 0: Dispatch normalized notification to Redux
          store.dispatch(setNotificationData({
            data: normalizedNotification,  // Use normalized structure
            normalizedType: messageType,
            timestamp: Date.now()
          }));

          // 🔄 Invalidate transaction history caches
          // Wallet updates often indicate deposit/withdrawal status changes
          // This ensures transaction history tables show real-time updates
          if (cacheUpdateService.queryClient) {
            console.log('🔄 Invalidating transaction history due to wallet update');

            // Invalidate all transaction history queries
            cacheUpdateService.queryClient.invalidateQueries(['fetchDepositTransanction'], {
              refetchType: 'active' // Only refetch if component is mounted and active
            });
            cacheUpdateService.queryClient.invalidateQueries(['fetchWithDrawTransactions'], {
              refetchType: 'active'
            });
            cacheUpdateService.queryClient.invalidateQueries(['fetchCryptoTransactionHistory'], {
              refetchType: 'active'
            });
            cacheUpdateService.queryClient.invalidateQueries(['fetchHarvestTransanction'], {
              refetchType: 'active'
            });
            cacheUpdateService.queryClient.invalidateQueries(['fetchShetabTransaction'], {
              refetchType: 'active'
            });
            cacheUpdateService.queryClient.invalidateQueries(['fetchTransaction'], {
              refetchType: 'active'
            });
          }
        } else if (messageType === 'ORDER' || data.type === 2) {
          // Order update
          // Message format: {"type":2,"id":133,"time":"2025-10-01 22:17:17","market":"TRXUSDT","order_type":"1","role":"1","order_price":"0.34153100",...}
          console.log('📋 Order update detected:', data);

          // 🔥 CRITICAL FIX: Clear in-memory order cache when order updates via WebSocket
          if (typeof window !== 'undefined' && window.orderCache) {
            const cacheSize = window.orderCache.size;
            window.orderCache.clear();
            console.log(`🗑️ Cleared in-memory order cache (${cacheSize} entries) due to WebSocket order update`);
          }

          store.dispatch(setOrderData({
            type: 'websocket_update',
            data: data,
            timestamp: Date.now()
          }));

          // 🔥 PHASE 0: Dispatch normalized notification to Redux
          store.dispatch(setNotificationData({
            data: normalizedNotification,  // Use normalized structure
            normalizedType: messageType,
            timestamp: Date.now()
          }));
        } else if (messageType === 'NOTIFICATION' || data.type === 0) {
          // General notification
          // Message format: {"type":0,"title":"Quick buy order...","content":"Your quick order number 130..."}
          console.log('🔔 General notification detected:', data);

          // ✅ CRITICAL FIX: Handle update_wallet and update_orders flags
          // The backend now sends unified notifications with flags instead of separate type-based messages
          const shouldUpdateWallet = data.update_wallet === true;
          const shouldUpdateOrders = data.update_orders === true;

          console.log('🔍 Notification flags:', {
            update_wallet: shouldUpdateWallet,
            update_orders: shouldUpdateOrders,
            silent: data.silent
          });

          // If update_wallet flag is set, also dispatch wallet update
          if (shouldUpdateWallet) {
            console.log('💰 Notification has update_wallet flag - dispatching wallet update');
            store.dispatch(setWalletData({
              type: 'websocket_update',
              data: data,
              timestamp: Date.now()
            }));
            // Also dispatch legacy action for backward compatibility
            store.dispatch(handleWalletUpdater());
          }

          // If update_orders flag is set, also dispatch order update
          if (shouldUpdateOrders) {
            console.log('📋 Notification has update_orders flag - dispatching order update');

            // 🔥 CRITICAL FIX: Clear in-memory order cache
            if (typeof window !== 'undefined' && window.orderCache) {
              const cacheSize = window.orderCache.size;
              window.orderCache.clear();
              console.log(`🗑️ Cleared in-memory order cache (${cacheSize} entries) due to update_orders flag`);
            }

            store.dispatch(setOrderData({
              type: 'websocket_update',
              data: data,
              timestamp: Date.now()
            }));
            // ✅ CRITICAL FIX: Also dispatch legacy action for backward compatibility
            // Many components still listen to state.notification.orderUpdater
            store.dispatch(handleOrderUpdater());
            console.log('✅ Dispatched handleOrderUpdater() for legacy components');
          }

          // 🔥 PHASE 0: Dispatch normalized notification to Redux
          store.dispatch(setNotificationData({
            data: normalizedNotification,  // Use normalized structure
            normalizedType: messageType,
            timestamp: Date.now()
          }));
        } else {
          console.warn('⚠️ Unknown notification type:', messageType, data);

          // 🔥 PHASE 0: Still normalize and dispatch unknown types
          store.dispatch(setNotificationData({
            data: normalizedNotification,  // Use normalized structure
            normalizedType: messageType,
            timestamp: Date.now()
          }));
        }

        // 🔥 PHASE 2: Mark message as successfully processed
        const processingTime = Date.now() - processingStartTime;
        this.messageProcessor.markProcessed(normalizedNotification.hash, processingTime);

        // 🔥 PHASE 2: Send acknowledgment to server
        if (normalizedNotification.id) {
          this.ackManager.sendAck(socket, normalizedNotification.id, {
            type: messageType,
            hash: normalizedNotification.hash
          }).catch(error => {
            console.warn(`⚠️ Failed to send ack for message ${normalizedNotification.id}:`, error.message);
          });
        }

      } catch (error) {
        console.error('❌ Error processing notification message:', error);

        // 🔥 PHASE 2: Mark message as failed
        if (normalizedNotification && normalizedNotification.hash) {
          this.messageProcessor.markFailed(normalizedNotification.hash, error);
        }
      }
    });
  }

  /**
   * Set up trading message handlers
   */
  setupTradingHandlers(socket) {
    const state = this.connectionStates.get('trading');
    const isIranServer = state?.currentServer === 'Iran';

    console.log(`🔧 Setting up trading handlers for ${state?.currentServer} server`);

    // Helper function to parse Iran server messages (JSON stringified)
    const parseIranMessage = (data) => {
      if (isIranServer && typeof data === 'string') {
        try {
          return JSON.parse(data);
        } catch (error) {
          console.error('❌ Error parsing Iran server message:', error);
          return data;
        }
      }
      return data;
    };

    // Market data handler - Iran uses 'market', Germany uses 'prices'
    const marketEvent = isIranServer ? 'market' : 'prices';
    socket.on(marketEvent, (data) => {
      const parsedData = parseIranMessage(data);
      logWebSocket(`${marketEvent} data received`, { dataLength: Array.isArray(parsedData) ? parsedData.length : 'single item' });

      // Throttle market data updates to prevent excessive Redux dispatches
      this.throttleUpdate('market_data', this.config.throttleMarketData, (throttledData) => {
        store.dispatch(setMarketData({
          type: 'market_update',
          data: throttledData,
          timestamp: Date.now()
        }));
      }, parsedData);
    });

    // Orderbook handler - Iran uses 'orderbook', Germany uses 'ask-bids'
    const orderbookEvent = isIranServer ? 'orderbook' : 'ask-bids';
    socket.on(orderbookEvent, (data) => {
      const parsedData = parseIranMessage(data);

      // Extract symbol from Iran server data if available
      let symbol = this.currentOrderbookSubscription;
      if (isIranServer && parsedData && parsedData.coin) {
        symbol = parsedData.coin;
        // Update current subscription to match the received symbol
        this.currentOrderbookSubscription = symbol;
      }

      logWebSocket(`${orderbookEvent} data received`, {
        symbol: symbol,
        hasAsks: !!(parsedData?.asks?.length),
        hasBids: !!(parsedData?.bids?.length),
        serverType: isIranServer ? 'Iran' : 'Germany'
      });

      // Add symbol context to orderbook data
      const orderbookWithSymbol = {
        symbol: symbol,
        orderbook: parsedData,
        timestamp: Date.now()
      };

      // Throttle orderbook updates to prevent excessive Redux dispatches
      this.throttleUpdate(`orderbook_${symbol}`, this.config.throttleOrderbook, (throttledData) => {
        // Dispatch to Redux store
        store.dispatch(setMarketData({
          type: 'orderbook_update',
          data: throttledData,
          timestamp: Date.now()
        }));

        // Also cache to sessionStorage for immediate access
        this.cacheOrderbookToSessionStorage(throttledData);
      }, orderbookWithSymbol);
    });

    // Deals/trades handler - both servers use 'deals'
    socket.on('deals', (data) => {
      const parsedData = parseIranMessage(data);

      // Extract deals array based on server type
      let dealsArray = parsedData;
      if (isIranServer && parsedData && typeof parsedData === 'object' && parsedData.data) {
        // Iran server format: { type: "deals", data: [...] }
        dealsArray = parsedData.data;
      }

      // Debug deals data reception
      logWebSocket('Deals data received', {
        symbol: this.currentDealsSubscription,
        dataType: typeof data,
        isArray: Array.isArray(dealsArray),
        count: Array.isArray(dealsArray) ? dealsArray.length : 'single item',
        serverType: this.connectionStates.get('trading')?.currentServer,
        rawStructure: isIranServer ? 'Iran format with data property' : 'Germany direct array'
      });

      // Add symbol context to deals data
      const dealsWithSymbol = {
        symbol: this.currentDealsSubscription,
        deals: dealsArray,
        timestamp: Date.now()
      };

      // Throttle deals updates to prevent excessive Redux dispatches
      this.throttleUpdate(`deals_${this.currentDealsSubscription}`, this.config.throttleDeals, (throttledData) => {
        // Dispatch to Redux store
        store.dispatch(setMarketData({
          type: 'deals_update',
          data: throttledData,
          timestamp: Date.now()
        }));

        // Also cache to sessionStorage for immediate access
        this.cacheDealsToSessionStorage(throttledData);
      }, dealsWithSymbol);
    });

    // Ticker handler (if available)
    socket.on('ticker', (data) => {
      const parsedData = parseIranMessage(data);
      logWebSocket('Ticker data received', { dataType: typeof parsedData });

      // Throttle ticker updates to prevent excessive Redux dispatches
      this.throttleUpdate('ticker', this.config.throttleTicker, (throttledData) => {
        store.dispatch(setMarketData({
          type: 'ticker_update',
          data: throttledData,
          timestamp: Date.now()
        }));
      }, parsedData);
    });

    // USDT price handlers - Iran server specific events
    if (isIranServer) {
      // Handle usdt-price events (Iran server format)
      socket.on('usdt-price', (data) => {
        const parsedData = parseIranMessage(data);
        console.log('💰 USDT price data received:', parsedData);
        store.dispatch(setMarketData({
          type: 'usdt_price_update',
          data: parsedData,
          timestamp: Date.now()
        }));
      });

      // Handle indexprice events (Iran server format)
      socket.on('indexprice', (data) => {
        const parsedData = parseIranMessage(data);
        if (process.env.NODE_ENV === 'development') {
          console.log(`💰 Index price data received: ${parsedData?.length || 0} items`);
        }
        store.dispatch(setMarketData({
          type: 'indexprice_update',
          data: parsedData,
          timestamp: Date.now()
        }));
      });
    } else {
      // Germany server USDT price handling
      socket.on('indexprice', (data) => {
        if (process.env.NODE_ENV === 'development') {
          console.log(`💰 Index price data received (Germany): ${Array.isArray(data) ? data.length + ' items' : 'single item'}`);
        }
        store.dispatch(setMarketData({
          type: 'indexprice_update',
          data: data,
          timestamp: Date.now()
        }));
      });
    }

    // Debug: Log events received (only when WebSocket debugging is enabled)
    socket.onAny((eventName, ...args) => {
      if (!['connect', 'disconnect', 'connect_error', 'ping', 'pong'].includes(eventName)) {
        // Only log event name and data size for frequent events like indexprice
        if (eventName === 'indexprice') {
          const dataSize = JSON.stringify(args).length;
          logWebSocket(`WebSocket event: ${eventName}`, { dataSize: `${dataSize} bytes` });
        } else {
          logWebSocket(`WebSocket event: ${eventName}`, { argsCount: args.length });
        }
      }
    });
  }

  /**
   * Schedule reconnection with exponential backoff + jitter
   * 🔥 PHASE 1: Enhanced with adaptive backoff based on connection quality
   */
  scheduleReconnect(serverKey) {
    const state = this.connectionStates.get(serverKey);

    if (state.reconnectAttempts >= this.config.maxReconnectAttempts) {
      console.error(`❌ Max reconnection attempts reached for ${serverKey}`);
      return;
    }

    state.reconnectAttempts++;

    // 🔥 PHASE 1: Use adaptive backoff with jitter based on connection quality
    const qualityMonitor = this.qualityMonitors.get(serverKey);
    const quality = qualityMonitor ? qualityMonitor.getQuality() : 'FAIR';

    const delay = getAdaptiveReconnectDelay(
      state.reconnectAttempts - 1,
      quality,
      this.config.reconnectDelay,
      this.config.maxReconnectDelay
    );

    console.log(`🔄 Scheduling reconnect for ${serverKey} in ${delay}ms (attempt ${state.reconnectAttempts}, quality: ${quality})`);

    if (qualityMonitor) {
      const metrics = qualityMonitor.getMetrics();
      console.log(`📊 Connection metrics:`, metrics);
    }

    setTimeout(() => {
      if (!state.connected) {
        state.connecting = true;
        this.connectionStates.set(serverKey, state);
        this.connect(serverKey);
      }
    }, delay);
  }

  /**
   * Update Redux connection state
   * 🔥 PHASE 1: Enhanced with connection quality and circuit breaker state
   */
  updateReduxConnectionState() {
    const connectionState = {};
    this.connectionStates.forEach((state, serverKey) => {
      // Get quality metrics
      const qualityMonitor = this.qualityMonitors.get(serverKey);
      const qualityMetrics = qualityMonitor ? qualityMonitor.getMetrics() : null;

      // Get circuit breaker state
      const circuitBreaker = this.circuitBreakers.get(serverKey);
      const circuitBreakerState = circuitBreaker ? circuitBreaker.getState() : null;

      connectionState[serverKey] = {
        ...state,
        // 🔥 PHASE 1: Add quality metrics
        quality: qualityMetrics,
        // 🔥 PHASE 1: Add circuit breaker state
        circuitBreaker: circuitBreakerState
      };
    });

    store.dispatch(setConnectionState(connectionState));
  }

  /**
   * Subscribe to a data stream
   */
  subscribe(serverKey, event, data = {}, callback = null) {
    const socket = this.connections.get(serverKey);
    if (!socket || !socket.connected) {
      console.warn(`⚠️ Cannot subscribe to ${event}: ${serverKey} not connected`);
      return null;
    }

    const state = this.connectionStates.get(serverKey);
    const isIranServer = state?.currentServer === 'Iran';

    const subscriptionKey = `${serverKey}:${event}:${JSON.stringify(data)}`;

    // Store subscription for restoration after reconnect
    this.subscriptions.set(subscriptionKey, {
      serverKey,
      event,
      data,
      callback,
      active: true,
      timestamp: Date.now()
    });

    // Set up listener if callback provided
    if (callback) {
      socket.on(event, callback);
    }

    // Prepare subscription data based on server type
    let subscriptionData = data;

    if (isIranServer) {
      // Iran server requires JSON stringified data for all events
      if (Object.keys(data).length === 0) {
        // For empty data, create default subscription based on event type
        const defaultData = { type: event };
        subscriptionData = JSON.stringify(defaultData);
      } else {
        // For events with data, stringify the data
        subscriptionData = JSON.stringify(data);
      }
    } else {
      // Germany server format: send symbol directly for orderbook/deals
      if (event === 'ask-bids' || event === 'deals') {
        // Germany server expects: ["ask-bids", "TRXUSDT"] or ["deals", "TRXUSDT"]
        subscriptionData = data.symbol || data.market_pair || 'TRXUSDT';
      }
      // For other events, use data as-is
    }

    // Track subscriptions for context-aware caching
    if (event === 'deals') {
      const symbol = data.symbol || data.market_pair || (typeof subscriptionData === 'string' ? subscriptionData : null);
      if (symbol) {
        this.currentDealsSubscription = symbol;
        logWebSocket(`Tracking deals subscription for symbol: ${symbol}`);
      } else {
        logWebSocket('Warning: Could not determine symbol for deals subscription', { data, subscriptionData });
      }
    } else if ((event === 'orderbook' || event === 'ask-bids')) {
      const symbol = data.symbol || data.market_pair || (typeof subscriptionData === 'string' ? subscriptionData : null);
      if (symbol) {
        this.currentOrderbookSubscription = symbol;
        logWebSocket(`Tracking orderbook subscription for symbol: ${symbol}`);
      } else {
        logWebSocket('Warning: Could not determine symbol for orderbook subscription', { data, subscriptionData });
      }
    }

    // Send subscription request
    socket.emit(event, subscriptionData);
    logWebSocket(`Subscribed to ${event} on ${serverKey} (${state?.currentServer}) with data:`, subscriptionData);

    // Additional debugging for deals subscriptions
    if (event === 'deals') {
      logWebSocket(`Deals subscription details:`, {
        serverType: state?.currentServer,
        originalData: data,
        finalSubscriptionData: subscriptionData,
        isGermanyServer: state?.currentServer === 'Germany'
      });
    }

    // Return unsubscribe function
    return () => this.unsubscribe(subscriptionKey, callback);
  }

  /**
   * Unsubscribe from a data stream
   */
  unsubscribe(subscriptionKey, callback = null) {
    const subscription = this.subscriptions.get(subscriptionKey);
    if (!subscription) return;

    const socket = this.connections.get(subscription.serverKey);
    if (!socket) return;

    // Remove local callback
    if (callback) {
      socket.off(subscription.event, callback);
    }

    // Send unsubscribe message to server
    const state = this.connectionStates.get(subscription.serverKey);
    const isIranServer = state?.currentServer === 'Iran';

    if (isIranServer) {
      // Iran server: send unsubscribe message with JSON format
      const unsubscribeData = {
        type: 'unsubscribe',
        event: subscription.event,
        ...subscription.data
      };
      socket.emit('unsubscribe', JSON.stringify(unsubscribeData));
      console.log(`📡 Sent unsubscribe to Iran server for ${subscription.event}:`, unsubscribeData);
    } else {
      // Germany server: send unsubscribe message
      socket.emit('unsubscribe', subscription.event, subscription.data);
      console.log(`📡 Sent unsubscribe to Germany server for ${subscription.event}:`, subscription.data);
    }

    // Mark subscription as inactive and remove from map
    this.subscriptions.delete(subscriptionKey);

    console.log(`📡 Unsubscribed from ${subscriptionKey}`);
  }

  /**
   * Restore subscriptions after reconnection
   */
  restoreSubscriptions(serverKey) {
    console.log(`🔄 Restoring subscriptions for ${serverKey}`);

    const socket = this.connections.get(serverKey);
    if (!socket || !socket.connected) return;

    const state = this.connectionStates.get(serverKey);
    const isIranServer = state?.currentServer === 'Iran';

    this.subscriptions.forEach((subscription, key) => {
      if (subscription.serverKey === serverKey && subscription.active) {
        // Prepare subscription data based on server type
        let subscriptionData = subscription.data;

        if (isIranServer) {
          // Iran server requires JSON stringified data for all events
          if (Object.keys(subscription.data).length === 0) {
            // For empty data, create default subscription based on event type
            const defaultData = { type: subscription.event };
            subscriptionData = JSON.stringify(defaultData);
          } else {
            // For events with data, stringify the data
            subscriptionData = JSON.stringify(subscription.data);
          }
        }

        // Re-emit subscription
        socket.emit(subscription.event, subscriptionData);

        // Re-attach callback if exists
        if (subscription.callback) {
          socket.on(subscription.event, subscription.callback);
        }

        console.log(`📡 Restored subscription: ${key} with data:`, subscriptionData);
      }
    });

    // For Iran server, also emit initial subscription requests
    if (isIranServer) {
      logWebSocket('Emitting initial Iran server subscriptions');
      setTimeout(() => {
        if (socket.connected) {
          socket.emit('market', JSON.stringify({ type: 'market' }));
          socket.emit('usdt-price', JSON.stringify({ type: 'usdt-price' }));
          socket.emit('indexprice', JSON.stringify({ type: 'indexprice' }));
          socket.emit('deals', JSON.stringify({ type: 'deals' }));
          socket.emit('orderbook', JSON.stringify({ type: 'orderbook', market_pair: 'TRXUSDT' }));
          logWebSocket('Initial Iran server subscriptions sent (market, usdt-price, indexprice, deals, orderbook)');
        }
      }, 500);
    }
  }

  /**
   * Start heartbeat for connection
   */
  startHeartbeat(socket, serverKey) {
    const heartbeatInterval = setInterval(() => {
      if (socket.connected) {
        socket.emit('ping', { timestamp: Date.now() });
      } else {
        clearInterval(heartbeatInterval);
      }
    }, this.config.heartbeatInterval);
  }

  /**
   * 🔥 PHASE 1: Enhanced heartbeat with connection quality monitoring
   * 🔥 PHASE 4 OPTIMIZATION: Use Socket.IO built-in heartbeat mechanism
   *
   * Socket.IO v4+ has built-in heartbeat at the transport level.
   * We monitor the engine-level ping/pong events and track application activity.
   */
  startEnhancedHeartbeat(socket, serverKey) {
    const qualityMonitor = this.qualityMonitors.get(serverKey);

    // Clear any existing heartbeat timer
    const existingTimer = this.heartbeatTimers.get(serverKey);
    if (existingTimer) {
      clearInterval(existingTimer);
    }

    console.log(`💓 Starting enhanced heartbeat for ${serverKey} using Socket.IO built-in mechanism`);

    // 🔥 PHASE 4: Initialize heartbeat metrics if not exists
    if (!this.heartbeatMetrics.has(serverKey)) {
      this.heartbeatMetrics.set(serverKey, new HeartbeatMetrics(serverKey, {
        windowSize: 100 // Track last 100 latency measurements
      }));
      console.log(`📊 Heartbeat metrics initialized for ${serverKey}`);
    }
    const metrics = this.heartbeatMetrics.get(serverKey);

    // Track last activity time
    let lastActivityTime = Date.now();
    let missedChecks = 0;
    const maxMissedChecks = 3;
    const checkInterval = 30000; // Check every 30 seconds
    const activityTimeout = 90000; // Consider stale if no activity for 90 seconds (3 × 30s)

    // 🔥 OPTIMIZATION: Monitor Socket.IO's built-in heartbeat at engine level
    const engine = socket.io?.engine;
    if (engine) {
      // Listen to Socket.IO's built-in ping event (transport-level)
      const pingHandler = () => {
        lastActivityTime = Date.now();
        missedChecks = 0;

        // Record in quality monitor
        if (qualityMonitor) {
          qualityMonitor.recordPingSent();
        }

        // 🔥 PHASE 4: Record in heartbeat metrics
        if (metrics) {
          metrics.recordPingSent();
        }

        console.log(`💓 Socket.IO ping sent for ${serverKey}`);
      };

      // Listen to Socket.IO's built-in pong event (transport-level)
      const pongHandler = (latency) => {
        lastActivityTime = Date.now();
        missedChecks = 0;

        // Record in quality monitor
        if (qualityMonitor) {
          qualityMonitor.recordPongReceived(latency);
        }

        // 🔥 PHASE 4: Record in heartbeat metrics
        if (metrics) {
          metrics.recordPongReceived(latency);
        }

        console.log(`💓 Socket.IO pong received for ${serverKey} (latency: ${latency}ms)`);
      };

      engine.on('ping', pingHandler);
      engine.on('pong', pongHandler);

      console.log(`✅ Monitoring Socket.IO built-in heartbeat for ${serverKey} (ping interval: ${engine.pingInterval || 'default'}ms, timeout: ${engine.pingTimeout || 'default'}ms)`);
    } else {
      console.warn(`⚠️ Socket.IO engine not available for ${serverKey}, using activity-based monitoring only`);
    }

    // Update last activity time on any message received
    socket.onAny(() => {
      lastActivityTime = Date.now();
      missedChecks = 0;

      // 🔥 PHASE 4: Record activity in heartbeat metrics
      if (metrics) {
        metrics.recordActivity();
      }
    });

    // Periodic check for connection staleness
    const heartbeatInterval = setInterval(() => {
      if (!socket.connected) {
        clearInterval(heartbeatInterval);
        this.heartbeatTimers.delete(serverKey);
        return;
      }

      const timeSinceLastActivity = Date.now() - lastActivityTime;
      const isStale = timeSinceLastActivity > activityTimeout;

      // 🔥 PHASE 4: Record staleness check in metrics
      if (metrics) {
        metrics.recordStalenessCheck(isStale);
      }

      // Check if connection is stale (no activity for too long)
      if (isStale) {
        missedChecks++;

        if (qualityMonitor) {
          qualityMonitor.recordMissedHeartbeat();
        }

        // 🔥 PHASE 4: Record missed ping in metrics
        if (metrics) {
          metrics.recordMissedPing();
        }

        console.warn(`⚠️ No activity for ${Math.round(timeSinceLastActivity / 1000)}s on ${serverKey} (${missedChecks}/${maxMissedChecks})`);

        if (missedChecks >= maxMissedChecks) {
          console.error(`❌ Connection stale for ${serverKey} - forcing reconnection`);

          // 🔥 PHASE 4: Handle connection stale error
          const error = new Error(`Connection stale: No activity for ${Math.round(timeSinceLastActivity / 1000)} seconds`);
          error.code = 'CONNECTION_STALE';

          this.errorHandler.handleError(error, {
            errorType: 'TIMEOUT_ERROR',
            context: {
              serverKey,
              timeSinceLastActivity,
              missedChecks,
              maxMissedChecks,
              operation: 'heartbeat_monitoring'
            },
            retryFn: () => {
              console.log(`🔄 Reconnecting ${serverKey} after connection stale...`);
              return this.connect(serverKey);
            }
          });

          // Clear heartbeat timer
          clearInterval(heartbeatInterval);
          this.heartbeatTimers.delete(serverKey);

          // Disconnect and schedule reconnection
          socket.disconnect();

          // Force reconnection after disconnect
          setTimeout(() => {
            console.log(`🔄 Reconnecting ${serverKey} after connection stale...`);
            this.scheduleReconnect(serverKey);
          }, 1000);
        }
      } else {
        // Connection is active, reset missed checks
        if (missedChecks > 0) {
          console.log(`✅ Connection active for ${serverKey}, resetting missed checks`);
          missedChecks = 0;
        }
      }
    }, checkInterval);

    // Store the timer so we can clear it later
    this.heartbeatTimers.set(serverKey, heartbeatInterval);

    console.log(`💓 Enhanced heartbeat monitoring started for ${serverKey} (check interval: ${checkInterval}ms, timeout: ${activityTimeout}ms)`);
  }

  /**
   * Disconnect from a server
   */
  disconnect(serverKey) {
    const socket = this.connections.get(serverKey);
    if (socket) {
      socket.disconnect();
      this.connections.delete(serverKey);
      console.log(`🔌 Disconnected from ${serverKey}`);
    }
  }

  /**
   * Disconnect from all servers
   */
  disconnectAll() {
    console.log('🔌 Disconnecting from all servers');
    this.connections.forEach((socket, serverKey) => {
      socket.disconnect();
    });
    this.connections.clear();
    this.subscriptions.clear();
  }

  /**
   * Get connection status
   */
  getConnectionStatus(serverKey = null) {
    if (serverKey) {
      return this.connectionStates.get(serverKey) || null;
    }
    
    const status = {};
    this.connectionStates.forEach((state, key) => {
      status[key] = { ...state };
    });
    return status;
  }

  /**
   * Check if service is healthy
   */
  isHealthy() {
    let connectedCount = 0;
    this.connectionStates.forEach(state => {
      if (state.connected) connectedCount++;
    });

    return connectedCount > 0;
  }

  /**
   * Get subscription status for debugging/monitoring
   */
  getSubscriptionStatus() {
    const activeSubscriptions = Array.from(this.subscriptions.values()).filter(sub => sub.active);

    return {
      total: this.subscriptions.size,
      active: activeSubscriptions.length,
      activeSubscribers: this.connections.size,
      subscriptions: activeSubscriptions.map(sub => ({
        key: `${sub.serverKey}:${sub.event}`,
        event: sub.event,
        serverKey: sub.serverKey,
        active: sub.active,
        timestamp: sub.timestamp
      }))
    };
  }

  /**
   * Test Iran server subscriptions (development only)
   */
  testIranSubscriptions() {
    if (process.env.NODE_ENV !== 'development') {
      console.warn('⚠️ Test functions only available in development mode');
      return;
    }

    const socket = this.connections.get('trading');
    if (!socket || !socket.connected) {
      console.error('❌ Trading socket not connected');
      return;
    }

    const state = this.connectionStates.get('trading');
    const isIranServer = state?.currentServer === 'Iran';

    if (!isIranServer) {
      console.warn('⚠️ Not connected to Iran server');
      return;
    }

    logWebSocket('Testing Iran server subscriptions...');

    // Test market subscription
    socket.emit('market', JSON.stringify({ type: 'market' }));
    logWebSocket('Sent market subscription');

    // Test USDT price subscription
    socket.emit('usdt-price', JSON.stringify({ type: 'usdt-price' }));
    logWebSocket('Sent usdt-price subscription');

    // Test indexprice subscription
    socket.emit('indexprice', JSON.stringify({ type: 'indexprice' }));
    logWebSocket('Sent indexprice subscription');

    // Test orderbook subscription (requires market_pair for Iran server)
    socket.emit('orderbook', JSON.stringify({ type: 'orderbook', market_pair: 'TRXUSDT' }));
    logWebSocket('Sent orderbook subscription for TRXUSDT');

    // Test deals subscription
    socket.emit('deals', JSON.stringify({ type: 'deals' }));
    console.log('📤 Sent deals subscription');

    console.log('✅ All test subscriptions sent. Check network tab and console for responses.');
  }

  /**
   * Cache deals data to sessionStorage for immediate access across pages
   */
  cacheDealsToSessionStorage(dealsData) {
    try {
      if (!dealsData) return;

      let symbol = null;
      let deals = [];

      // Extract symbol and deals from the new structure
      if (dealsData.symbol && Array.isArray(dealsData.deals)) {
        // New structure: { symbol: 'TRXUSDT', deals: [...], timestamp: ... }
        symbol = dealsData.symbol;
        deals = dealsData.deals;
      } else if (Array.isArray(dealsData)) {
        // Fallback: Data is array of deals (use current subscription context)
        symbol = this.currentDealsSubscription;
        deals = dealsData;
      }

      if (symbol && deals.length > 0) {
        const cacheKey = `trading_deals_${symbol}`;
        const cacheItem = {
          data: deals,
          timestamp: Date.now(),
          expiry: 5 * 60 * 1000 // 5 minutes
        };

        sessionStorage.setItem(cacheKey, JSON.stringify(cacheItem));
        console.log(`🌍 Auto-cached deals for ${symbol}:`, deals.length, 'deals');

        // Only cache the exact symbol to avoid conflicts between TRXUSDT/TRXIRT etc.
        // No symbol variations to prevent data corruption
      } else {
        console.warn('🌍 Could not cache deals - missing symbol or deals:', {
          hasSymbol: !!symbol,
          dealsCount: deals.length,
          currentSubscription: this.currentDealsSubscription,
          dataStructure: dealsData
        });
      }
    } catch (error) {
      console.error('🌍 Error caching deals to sessionStorage:', error);
    }
  }

  /**
   * Cache orderbook data to sessionStorage for immediate access across pages
   */
  cacheOrderbookToSessionStorage(orderbookData) {
    try {
      if (!orderbookData) return;

      let symbol = null;
      let orderbook = null;

      // Extract symbol and orderbook from the new structure
      if (orderbookData.symbol && orderbookData.orderbook) {
        // New structure: { symbol: 'TRXUSDT', orderbook: {...}, timestamp: ... }
        symbol = orderbookData.symbol;
        orderbook = orderbookData.orderbook;
      } else if (orderbookData.asks && orderbookData.bids) {
        // Fallback: Data is orderbook object (use current subscription context)
        symbol = this.currentOrderbookSubscription;
        orderbook = orderbookData;
      }

      if (symbol && orderbook && (orderbook.asks || orderbook.bids)) {
        const cacheKey = `trading_orderbook_${symbol}`;
        const cacheItem = {
          data: orderbook,
          timestamp: Date.now(),
          expiry: 30 * 1000, // 30 seconds (orderbook updates frequently)
          forceRefresh: Date.now() // Add force refresh timestamp
        };

        sessionStorage.setItem(cacheKey, JSON.stringify(cacheItem));
        console.log(`🌍 Auto-cached orderbook for ${symbol}:`, {
          asks: orderbook.asks?.length || 0,
          bids: orderbook.bids?.length || 0,
          forceRefresh: cacheItem.forceRefresh
        });

        // Only cache the exact symbol to avoid conflicts between TRXUSDT/TRXIRT etc.
        // No symbol variations to prevent data corruption
      } else {
        console.warn('🌍 Could not cache orderbook - missing symbol or data:', {
          hasSymbol: !!symbol,
          hasOrderbook: !!orderbook,
          currentSubscription: this.currentOrderbookSubscription,
          dataStructure: orderbookData
        });
      }
    } catch (error) {
      console.error('🌍 Error caching orderbook to sessionStorage:', error);
    }
  }

  /**
   * Get connection states for debugging
   */
  getConnectionStates() {
    const states = {};
    this.connectionStates.forEach((state, key) => {
      states[key] = state;
    });
    return states;
  }

  /**
   * 🔥 PHASE 2: Get message validation statistics
   */
  getValidationStats() {
    return this.validationStats.getStats();
  }

  /**
   * 🔥 PHASE 2: Get message processing statistics
   */
  getProcessingStats() {
    return this.messageProcessor.getStats();
  }

  /**
   * 🔥 PHASE 2: Get acknowledgment statistics
   */
  getAcknowledgmentStats() {
    return this.ackManager.getStats();
  }

  /**
   * 🔥 PHASE 2: Get all Phase 2 statistics
   */
  getPhase2Stats() {
    return {
      validation: this.getValidationStats(),
      processing: this.getProcessingStats(),
      acknowledgment: this.getAcknowledgmentStats()
    };
  }

  /**
   * 🔥 PHASE 3: Load persisted state and reconcile with server
   */
  loadAndReconcileState() {
    console.log('🔄 Loading persisted state and reconciling...');

    try {
      // Load persisted notifications
      const persistedNotifications = this.stateManager.loadNotifications();
      const persistedUnreadCount = this.stateManager.loadUnreadCount();

      if (!persistedNotifications && persistedUnreadCount === null) {
        console.log('📭 No persisted state found');
        return;
      }

      // Get current state from Redux
      const currentState = {
        notifications: persistedNotifications || [],
        unreadCount: persistedUnreadCount || 0,
        timestamp: Date.now()
      };

      console.log(`📬 Loaded persisted state: ${currentState.notifications.length} notifications, ${currentState.unreadCount} unread`);

      // Note: Server state will be fetched via React Query auto-refresh
      // When server data arrives, it will be reconciled automatically

      // For now, restore the persisted state to Redux
      if (persistedNotifications && persistedNotifications.length > 0) {
        // Dispatch to Redux to restore notifications
        console.log('✅ Restored persisted notifications to state');
      }

    } catch (error) {
      console.error('❌ Failed to load and reconcile state:', error);
    }
  }

  /**
   * 🔥 PHASE 3: Save current state to localStorage
   */
  saveCurrentState() {
    console.log('💾 Saving current state...');

    try {
      // Get current state from Redux
      // Note: In a real implementation, you would get this from Redux store
      // For now, we'll save empty state as a placeholder

      const currentNotifications = []; // TODO: Get from Redux
      const currentUnreadCount = 0; // TODO: Get from Redux

      // Save to localStorage
      this.stateManager.saveNotifications(currentNotifications);
      this.stateManager.saveUnreadCount(currentUnreadCount);

      console.log(`✅ Saved state: ${currentNotifications.length} notifications, ${currentUnreadCount} unread`);

    } catch (error) {
      console.error('❌ Failed to save current state:', error);
    }
  }

  /**
   * 🔥 PHASE 3: Reconcile local and server state
   */
  reconcileState(localState, serverState) {
    console.log('🔄 Reconciling local and server state...');

    try {
      const result = this.reconciliationManager.reconcile(
        localState,
        serverState,
        MergeStrategy.MERGE_UNION
      );

      if (result.success) {
        console.log(`✅ State reconciliation successful (${result.conflicts.length} conflicts resolved)`);

        // Save reconciled state
        this.stateManager.saveNotifications(result.state.notifications || []);
        this.stateManager.saveUnreadCount(result.state.unreadCount || 0);

        return result.state;
      } else {
        console.error('❌ State reconciliation failed:', result.error);
        return serverState; // Fallback to server state
      }

    } catch (error) {
      console.error('❌ Failed to reconcile state:', error);
      return serverState; // Fallback to server state
    }
  }

  /**
   * 🔥 PHASE 3: Apply optimistic update
   */
  applyOptimisticUpdate(updateId, updateFn, currentState) {
    console.log(`⚡ Applying optimistic update: ${updateId}`);

    try {
      const updatedState = this.reconciliationManager.applyOptimisticUpdate(
        updateId,
        updateFn,
        currentState
      );

      // Save optimistic state
      this.stateManager.saveNotifications(updatedState.notifications || []);
      this.stateManager.saveUnreadCount(updatedState.unreadCount || 0);

      return updatedState;

    } catch (error) {
      console.error('❌ Failed to apply optimistic update:', error);
      return currentState;
    }
  }

  /**
   * 🔥 PHASE 3: Confirm optimistic update
   */
  confirmOptimisticUpdate(updateId) {
    console.log(`✅ Confirming optimistic update: ${updateId}`);
    return this.reconciliationManager.confirmOptimisticUpdate(updateId);
  }

  /**
   * 🔥 PHASE 3: Revert optimistic update
   */
  revertOptimisticUpdate(updateId) {
    console.log(`↩️ Reverting optimistic update: ${updateId}`);

    const originalState = this.reconciliationManager.revertOptimisticUpdate(updateId);

    if (originalState) {
      // Restore original state
      this.stateManager.saveNotifications(originalState.notifications || []);
      this.stateManager.saveUnreadCount(originalState.unreadCount || 0);
    }

    return originalState;
  }

  /**
   * 🔥 PHASE 3: Get state management statistics
   */
  getStateStats() {
    return this.stateManager.getStats();
  }

  /**
   * 🔥 PHASE 3: Get reconciliation statistics
   */
  getReconciliationStats() {
    return this.reconciliationManager.getStats();
  }

  /**
   * 🔥 PHASE 3: Get all Phase 3 statistics
   */
  getPhase3Stats() {
    return {
      state: this.getStateStats(),
      reconciliation: this.getReconciliationStats()
    };
  }

  /**
   * 🔥 PHASE 4: Get heartbeat metrics for a specific server
   */
  getHeartbeatMetrics(serverKey) {
    const metrics = this.heartbeatMetrics.get(serverKey);
    return metrics ? metrics.getMetrics() : null;
  }

  /**
   * 🔥 PHASE 4: Get heartbeat metrics for all servers
   */
  getAllHeartbeatMetrics() {
    const allMetrics = {};
    this.heartbeatMetrics.forEach((metrics, serverKey) => {
      allMetrics[serverKey] = metrics.getMetrics();
    });
    return allMetrics;
  }

  /**
   * 🔥 PHASE 4: Get error handler statistics
   */
  getErrorStats() {
    return this.errorHandler.getStats();
  }

  /**
   * 🔥 PHASE 4: Get graceful degradation status
   */
  getDegradationStatus() {
    return {
      healthStatus: this.degradationManager.getHealthStatus(),
      features: {
        realTimeNotifications: this.degradationManager.isFeatureEnabled('REAL_TIME_NOTIFICATIONS'),
        notificationHistory: this.degradationManager.isFeatureEnabled('NOTIFICATION_HISTORY'),
        notificationBadges: this.degradationManager.isFeatureEnabled('NOTIFICATION_BADGES'),
        toastNotifications: this.degradationManager.isFeatureEnabled('TOAST_NOTIFICATIONS'),
        soundNotifications: this.degradationManager.isFeatureEnabled('SOUND_NOTIFICATIONS')
      }
    };
  }

  /**
   * 🔥 PHASE 4: Get all Phase 4 statistics
   */
  getPhase4Stats() {
    return {
      heartbeat: this.getAllHeartbeatMetrics(),
      errors: this.getErrorStats(),
      degradation: this.getDegradationStatus()
    };
  }

  /**
   * Setup sleep/wake detection to force UI refresh after laptop sleep
   * Can be disabled via REACT_APP_PERF_DISABLE_SLEEP_WAKE_DETECTION for performance testing
   * Optimized to minimize performance impact
   */
  setupSleepWakeDetection() {
    // Check if sleep/wake detection is disabled for performance testing
    if (PERFORMANCE_TEST_CONFIG.DISABLE_SLEEP_WAKE_DETECTION) {
      logPerformanceTest('Sleep/Wake Detection', true);
      console.warn('🧪 PERFORMANCE TEST: Sleep/wake detection is DISABLED');
      return;
    }

    // Track activity time with throttled update (avoid updating on every mouse move)
    let activityUpdateScheduled = false;
    const updateActivity = () => {
      if (!activityUpdateScheduled) {
        activityUpdateScheduled = true;
        // Use requestAnimationFrame for better performance
        requestAnimationFrame(() => {
          this.lastActivityTime = Date.now();
          activityUpdateScheduled = false;
        });
      }
    };

    // Listen for various activity events with passive listeners for better scroll performance
    // Using fewer, more meaningful events to reduce overhead
    ['mousedown', 'keypress', 'touchstart', 'click'].forEach(event => {
      document.addEventListener(event, updateActivity, { passive: true });
    });

    // Check for sleep/wake every 10 seconds (reduced from 5s for better performance)
    // This is sufficient since we also have visibility change detection
    setInterval(() => {
      const now = Date.now();
      const timeSinceLastActivity = now - this.lastActivityTime;

      // If more than sleep threshold has passed, assume laptop was sleeping
      if (timeSinceLastActivity > this.sleepThreshold) {
        logInfo('Sleep/wake detected via activity timeout - reconnecting WebSockets', {
          timeSinceLastActivity: `${(timeSinceLastActivity / 1000).toFixed(1)}s`,
          threshold: `${(this.sleepThreshold / 1000).toFixed(1)}s`
        });

        this.handleSleepWakeRecovery();
        this.lastActivityTime = now; // Reset activity time
      }
    }, 10000); // Increased from 5000ms to 10000ms for better performance

    // Page Visibility API - PRIMARY detection method (most reliable)
    // This catches laptop sleep, tab switching, browser minimization
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        const now = Date.now();
        const timeSinceLastActivity = now - this.lastActivityTime;

        // Always update activity time when page becomes visible
        this.lastActivityTime = now;

        // If page was hidden for more than 30 seconds, likely laptop was sleeping
        if (timeSinceLastActivity > 30000) {
          console.log('👁️ Page visible after extended period - likely sleep/wake scenario');
          // Use setTimeout to avoid blocking the visibility change event
          setTimeout(() => this.handleSleepWakeRecovery(), 100);
        } else {
          console.log('👁️ Tab became visible - checking WebSocket connections');
          // Quick check for short absences
          setTimeout(() => this.handleSleepWakeRecovery(), 100);
        }
      }
    });

    // Listen for online/offline events (network recovery)
    window.addEventListener('online', () => {
      console.log('🌐 Network connection restored - reconnecting WebSockets');
      // Delay to allow network to stabilize
      setTimeout(() => this.handleSleepWakeRecovery(), 500);
    });

    window.addEventListener('offline', () => {
      console.log('🌐 Network connection lost');
      // No action needed - WebSocket will handle disconnect
    });
  }

  /**
   * Handle sleep/wake recovery - reconnect WebSockets and refresh UI
   * Throttled to prevent rapid repeated calls
   */
  handleSleepWakeRecovery() {
    const now = Date.now();

    // Throttle: Don't run if called within the last 5 seconds
    if (now - this.lastRecoveryTime < this.recoveryThrottle) {
      console.log(`⏭️ Skipping recovery - last recovery was ${((now - this.lastRecoveryTime) / 1000).toFixed(1)}s ago`);
      return;
    }

    this.lastRecoveryTime = now;
    console.log('🔄 Handling sleep/wake recovery...');

    // Pre-calculate server indices for staggered reconnection (avoid O(n²) complexity)
    const serverKeys = Array.from(this.connectionStates.keys());
    const serverIndexMap = new Map(serverKeys.map((key, index) => [key, index]));

    // Check all WebSocket connections and reconnect if needed
    this.connectionStates.forEach((state, serverKey) => {
      const socket = this.connections.get(serverKey);

      // Check if socket exists and is actually connected
      const isActuallyConnected = socket && socket.connected;

      if (!isActuallyConnected) {
        console.log(`🔄 WebSocket ${serverKey} is disconnected - reconnecting...`);

        // Reset reconnect attempts to allow fresh reconnection
        state.reconnectAttempts = 0;
        state.connecting = false;
        this.connectionStates.set(serverKey, state);

        // Disconnect old socket if it exists
        if (socket) {
          try {
            socket.disconnect();
          } catch (error) {
            console.error(`Error disconnecting old socket for ${serverKey}:`, error);
          }
          this.connections.delete(serverKey);
        }

        // Reconnect with fresh connection using pre-calculated index (O(1) lookup)
        const serverIndex = serverIndexMap.get(serverKey) || 0;
        setTimeout(() => {
          console.log(`🔄 Initiating reconnection for ${serverKey}...`);
          this.connect(serverKey);
        }, 500 * (serverIndex + 1)); // Stagger reconnections
      } else {
        console.log(`✅ WebSocket ${serverKey} is still connected`);
      }
    });

    // Force UI refresh
    this.forceUIRefresh();
  }

  /**
   * Force UI refresh by triggering all registered callbacks
   */
  forceUIRefresh() {
    console.log('🔄 Forcing UI refresh for all components');

    // Trigger all registered refresh callbacks
    this.forceRefreshCallbacks.forEach(callback => {
      try {
        callback();
      } catch (error) {
        console.error('🔄 Error in force refresh callback:', error);
      }
    });

    // Also dispatch Redux action to force re-renders
    if (this.store) {
      this.store.dispatch({
        type: 'websocket/forceRefresh',
        payload: { timestamp: Date.now() }
      });
    }
  }

  /**
   * Register a callback to be called when UI refresh is needed
   */
  registerForceRefreshCallback(callback) {
    this.forceRefreshCallbacks.add(callback);
    return () => this.forceRefreshCallbacks.delete(callback);
  }

  /**
   * Setup token change detection to reconnect when token is refreshed
   */
  setupTokenChangeDetection() {
    // Listen for storage events (token updates from other tabs or direct localStorage changes)
    window.addEventListener('storage', (event) => {
      if (event.key === 'userData') {
        console.log('🔐 Token change detected in localStorage (storage event)');

        const newUserData = event.newValue ? JSON.parse(event.newValue) : null;
        const oldUserData = event.oldValue ? JSON.parse(event.oldValue) : null;

        // Check if token actually changed
        if (newUserData?.accessToken !== oldUserData?.accessToken) {
          console.log('🔐 Access token changed - reconnecting notification socket...');
          this.reconnectNotificationSocket();
        }

        // Handle logout (userData removed)
        if (!newUserData && oldUserData) {
          console.log('🔐 User logged out - disconnecting notification socket...');
          this.disconnect('notifications');
        }

        // Handle login (userData added)
        if (newUserData && !oldUserData) {
          console.log('🔐 User logged in - connecting notification socket...');
          setTimeout(() => {
            this.connect('notifications');
          }, 500);
        }
      }
    });

    // Listen for custom token refresh events (dispatched by jwtService)
    window.addEventListener('tokenRefreshed', (event) => {
      console.log('🔐 Token refresh event detected (custom event)');
      this.reconnectNotificationSocket();
    });

    // Listen for manual token update events
    window.addEventListener('authTokenUpdated', (event) => {
      console.log('🔐 Auth token updated event detected');
      this.reconnectNotificationSocket();
    });

    // ✅ FIX: Listen for same-tab login events (dispatched by Redux handleLogin)
    // The 'storage' event only fires in other tabs, not the current tab
    // This ensures notification socket connects immediately on first login
    window.addEventListener('userLoggedIn', (event) => {
      console.log('🔐 User logged in event detected (same tab)');
      const userData = event.detail?.userData;

      if (userData?.accessToken) {
        console.log('🔐 User logged in with valid token - connecting notification socket...');
        setTimeout(() => {
          this.connect('notifications');
        }, 500);
      }
    });

    console.log('🔐 Token change detection initialized');
  }

  /**
   * Reconnect notification socket with fresh token
   */
  reconnectNotificationSocket() {
    if (this.connections.has('notifications')) {
      console.log('🔐 Reconnecting notification socket with fresh token...');

      // Check if token actually changed
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      const newToken = userData.accessToken;
      const lastToken = this.lastUsedTokens.get('notifications');

      if (newToken && newToken !== lastToken) {
        console.log('🔐 Token has changed - resetting auth error count');
        // Reset auth error count since we have a new token
        this.authErrorCounts.set('notifications', 0);
      } else if (newToken === lastToken) {
        console.warn('⚠️ Token has not changed - reconnection may fail again');
      }

      const socket = this.connections.get('notifications');

      // Disconnect current socket
      socket.disconnect();

      // Remove from connections map
      this.connections.delete('notifications');

      // Wait a bit for disconnect to complete, then reconnect with fresh token
      setTimeout(() => {
        console.log('🔐 Establishing new connection with fresh token...');
        this.connect('notifications');
      }, 500);
    } else {
      console.log('🔐 No notification socket to reconnect');
    }
  }

  /**
   * Setup event handlers for market namespace
   */
  setupMarketHandlers(socket, serverKey) {
    const state = this.connectionStates.get(serverKey);

    socket.on('connect', () => {
      logInfo(`Connected to ${serverKey} namespace (/market)`);

      // Update connection state
      state.connected = true;
      state.connecting = false;
      state.error = null;
      state.lastConnected = Date.now();
      state.reconnectAttempts = 0;
      this.connectionStates.set(serverKey, state);

      // Dispatch connection state to Redux
      store.dispatch(setConnectionState({
        server: serverKey,
        connected: true,
        error: null
      }));

      console.log(`✅ Market namespace connected successfully`);

      // Resubscribe to all active channels
      this.resubscribeMarketChannels();
    });

    socket.on('disconnect', (reason) => {
      logInfo(`Disconnected from ${serverKey} namespace: ${reason}`);

      state.connected = false;
      state.error = reason;
      this.connectionStates.set(serverKey, state);

      store.dispatch(setConnectionState({
        server: serverKey,
        connected: false,
        error: reason
      }));

      // Auto-reconnect if not a manual disconnect
      if (reason !== 'io client disconnect') {
        this.handleMarketReconnect(serverKey);
      }
    });

    socket.on('connect_error', (error) => {
      logError(`Connection error for ${serverKey} namespace:`, error);

      state.error = error.message;
      state.connecting = false;
      this.connectionStates.set(serverKey, state);

      store.dispatch(setConnectionState({
        server: serverKey,
        connected: false,
        error: error.message
      }));
    });

    // Market data event handlers
    socket.on('orderbook-updates', (data) => {
      const callbacks = this.marketCallbacks.orderbook.get(data.market_name);
      if (callbacks) {
        const transformedData = this.transformOrderbookData(data);
        callbacks.forEach(callback => callback(transformedData));
      }
    });

    socket.on('trades', (data) => {
      const callbacks = this.marketCallbacks.trades.get(data.market_name);
      if (callbacks) {
        const transformedData = this.transformTradeData(data);
        callbacks.forEach(callback => callback(transformedData));
      }
    });

    socket.on('market-data', (data) => {
      const callbacks = this.marketCallbacks.marketData.get(data.market_name);
      if (callbacks) {
        const transformedData = this.transformMarketData(data);
        callbacks.forEach(callback => callback(transformedData));
      }
    });

    socket.on('kline-data', (data) => {
      const key = `${data.market_name}@${data.interval}`;
      const callbacks = this.marketCallbacks.kline.get(key);
      if (callbacks) {
        callbacks.forEach(callback => callback(data));
      }
    });

    socket.on('order-events', (data) => {
      const callbacks = this.marketCallbacks.orderEvents.get(data.market_name);
      if (callbacks) {
        callbacks.forEach(callback => callback(data));
      }
    });

    console.log(`✅ Market namespace event handlers setup complete`);
  }

  /**
   * Handle market namespace reconnection
   */
  handleMarketReconnect(serverKey) {
    const state = this.connectionStates.get(serverKey);
    state.reconnectAttempts = (state.reconnectAttempts || 0) + 1;

    const delay = Math.min(1000 * Math.pow(2, state.reconnectAttempts), 30000);

    console.log(`🔄 Reconnecting to ${serverKey} namespace in ${delay}ms (attempt ${state.reconnectAttempts})`);

    setTimeout(() => {
      this.connect(serverKey);
    }, delay);
  }

  /**
   * Resubscribe to all active market channels after reconnection
   */
  resubscribeMarketChannels() {
    const socket = this.connections.get('market');
    if (!socket || !socket.connected) return;

    const channels = Array.from(this.marketSubscriptions.keys());
    if (channels.length > 0) {
      console.log(`📡 Resubscribing to ${channels.length} market channels:`, channels);
      socket.emit('subscribe', channels);
    }
  }

  /**
   * Subscribe to market orderbook updates
   */
  subscribeToMarketOrderbook(marketName, callback) {
    const channel = `orderbook-updates@${marketName}`;

    // Add callback
    if (!this.marketCallbacks.orderbook.has(marketName)) {
      this.marketCallbacks.orderbook.set(marketName, new Set());
    }
    this.marketCallbacks.orderbook.get(marketName).add(callback);

    // Track subscription
    if (!this.marketSubscriptions.has(channel)) {
      this.marketSubscriptions.set(channel, new Set());
    }
    this.marketSubscriptions.get(channel).add(callback);

    // Subscribe if connected
    const socket = this.connections.get('market');
    if (socket && socket.connected) {
      socket.emit('subscribe', [channel]);
      console.log(`📡 Subscribed to ${channel}`);
    }

    // Return unsubscribe function
    return () => {
      this.marketCallbacks.orderbook.get(marketName)?.delete(callback);
      this.marketSubscriptions.get(channel)?.delete(callback);

      // Unsubscribe if no more callbacks
      if (this.marketSubscriptions.get(channel)?.size === 0) {
        this.marketSubscriptions.delete(channel);
        if (socket && socket.connected) {
          socket.emit('unsubscribe', [channel]);
          console.log(`📡 Unsubscribed from ${channel}`);
        }
      }
    };
  }

  /**
   * Subscribe to market trades
   */
  subscribeToMarketTrades(marketName, callback) {
    const channel = `trades@${marketName}`;

    if (!this.marketCallbacks.trades.has(marketName)) {
      this.marketCallbacks.trades.set(marketName, new Set());
    }
    this.marketCallbacks.trades.get(marketName).add(callback);

    if (!this.marketSubscriptions.has(channel)) {
      this.marketSubscriptions.set(channel, new Set());
    }
    this.marketSubscriptions.get(channel).add(callback);

    const socket = this.connections.get('market');
    if (socket && socket.connected) {
      socket.emit('subscribe', [channel]);
      console.log(`📡 Subscribed to ${channel}`);
    }

    return () => {
      this.marketCallbacks.trades.get(marketName)?.delete(callback);
      this.marketSubscriptions.get(channel)?.delete(callback);

      if (this.marketSubscriptions.get(channel)?.size === 0) {
        this.marketSubscriptions.delete(channel);
        if (socket && socket.connected) {
          socket.emit('unsubscribe', [channel]);
          console.log(`📡 Unsubscribed from ${channel}`);
        }
      }
    };
  }

  /**
   * Subscribe to market data (24h statistics)
   */
  subscribeToMarketData(marketName, callback) {
    const channel = `market-data@${marketName}`;

    if (!this.marketCallbacks.marketData.has(marketName)) {
      this.marketCallbacks.marketData.set(marketName, new Set());
    }
    this.marketCallbacks.marketData.get(marketName).add(callback);

    if (!this.marketSubscriptions.has(channel)) {
      this.marketSubscriptions.set(channel, new Set());
    }
    this.marketSubscriptions.get(channel).add(callback);

    const socket = this.connections.get('market');
    if (socket && socket.connected) {
      socket.emit('subscribe', [channel]);
      console.log(`📡 Subscribed to ${channel}`);
    }

    return () => {
      this.marketCallbacks.marketData.get(marketName)?.delete(callback);
      this.marketSubscriptions.get(channel)?.delete(callback);

      if (this.marketSubscriptions.get(channel)?.size === 0) {
        this.marketSubscriptions.delete(channel);
        if (socket && socket.connected) {
          socket.emit('unsubscribe', [channel]);
          console.log(`📡 Unsubscribed from ${channel}`);
        }
      }
    };
  }

  /**
   * Transform Kafka orderbook data to BitPanel format
   */
  transformOrderbookData(data) {
    return {
      asks: data.asks || [],
      bids: data.bids || [],
      lastPrice: data.bids?.[0]?.price || data.asks?.[0]?.price,
      coin: data.market_name,
      timestamp: Date.now()
    };
  }

  /**
   * Transform Kafka trade data to BitPanel format
   */
  transformTradeData(data) {
    return {
      id: data.trade_id,
      price: parseFloat(data.price),
      amount: parseFloat(data.quantity),
      side: data.side,
      time: new Date(data.timestamp).getTime() / 1000,
      coin: data.market_name
    };
  }

  /**
   * Transform Kafka market data to BitPanel format
   */
  transformMarketData(data) {
    return {
      coin: data.market_name,
      lastPrice: parseFloat(data.last_price),
      high24h: parseFloat(data['24h_high']),
      low24h: parseFloat(data['24h_low']),
      volume24h: parseFloat(data['24h_volume']),
      change24h: parseFloat(data['24h_change']),
      changePercent24h: parseFloat(data['24h_change_percent']),
      timestamp: Date.now()
    };
  }

  /**
   * Get market namespace connection status
   */
  getMarketConnectionStatus() {
    return this.connectionStates.get('market') || {
      connected: false,
      connecting: false,
      error: null
    };
  }
}

// Export singleton instance
const webSocketService = new WebSocketService();
export default webSocketService;
